import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Chip,
  Breadcrumbs,
  Link as MuiLink,
  InputAdornment,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import TableChartIcon from '@mui/icons-material/TableChart';
import PrintIcon from '@mui/icons-material/Print';
import ClearIcon from '@mui/icons-material/Clear';
import TablePagination from '@mui/material/TablePagination';

interface InterestRate {
  rateId: string;
  rateType: string;
  valueDate: Date;
  rate: number;
  status: 'Active' | 'Inactive';
  creationDate: Date;
  lastUpdatedDate: Date;
  createdBy: string;
  lastUpdatedBy: string;
}

const InterestRateMaintenance: React.FC = () => {
  const [rates, setRates] = useState<InterestRate[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRate, setSelectedRate] = useState<InterestRate | null>(null);
  const [dialogType, setDialogType] = useState<'add' | 'edit' | 'view' | 'delete'>('add');
  const [searchRateType, setSearchRateType] = useState('');
  const [searchValueDate, setSearchValueDate] = useState<Date | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [formData, setFormData] = useState({
    rateType: '',
    valueDate: new Date(),
    rate: '',
    status: 'Active',
  });

  const rateTypes = ['3 Month Saibor', '6 Month Saibor', 'Other'];

  useEffect(() => {
    const mockRates: InterestRate[] = [
      {
        rateId: 'RATE001',
        rateType: '3 Month Saibor',
        valueDate: new Date('2024-01-01'),
        rate: 5.2500,
        status: 'Active',
        creationDate: new Date('2024-01-01'),
        lastUpdatedDate: new Date('2024-01-01'),
        createdBy: 'Admin',
        lastUpdatedBy: 'Admin',
      },
      {
        rateId: 'RATE002',
        rateType: '6 Month Saibor',
        valueDate: new Date('2024-02-01'),
        rate: 5.5000,
        status: 'Active',
        creationDate: new Date('2024-02-01'),
        lastUpdatedDate: new Date('2024-02-01'),
        createdBy: 'Admin',
        lastUpdatedBy: 'Admin',
      },
      {
        rateId: 'RATE003',
        rateType: 'Other',
        valueDate: new Date('2024-03-01'),
        rate: 4.9000,
        status: 'Inactive',
        creationDate: new Date('2024-03-01'),
        lastUpdatedDate: new Date('2024-03-01'),
        createdBy: 'Admin',
        lastUpdatedBy: 'Admin',
      },
      {
        rateId: 'RATE004',
        rateType: '3 Month Saibor',
        valueDate: new Date('2024-04-01'),
        rate: 5.1000,
        status: 'Active',
        creationDate: new Date('2024-04-01'),
        lastUpdatedDate: new Date('2024-04-01'),
        createdBy: 'Admin',
        lastUpdatedBy: 'Admin',
      },
      {
        rateId: 'RATE005',
        rateType: '6 Month Saibor',
        valueDate: new Date('2024-05-01'),
        rate: 5.6000,
        status: 'Inactive',
        creationDate: new Date('2024-05-01'),
        lastUpdatedDate: new Date('2024-05-01'),
        createdBy: 'Admin',
        lastUpdatedBy: 'Admin',
      },
      {
        rateId: 'RATE006',
        rateType: 'Other',
        valueDate: new Date('2024-06-01'),
        rate: 4.8000,
        status: 'Active',
        creationDate: new Date('2024-06-01'),
        lastUpdatedDate: new Date('2024-06-01'),
        createdBy: 'Admin',
        lastUpdatedBy: 'Admin',
      },
    ];
    setRates(mockRates);
  }, []);

  const handleOpenDialog = (type: 'add' | 'edit' | 'view' | 'delete', rate?: InterestRate) => {
    setDialogType(type);
    if (rate) {
      setSelectedRate(rate);
      setFormData({
        rateType: rate.rateType,
        valueDate: rate.valueDate,
        rate: rate.rate.toString(),
        status: rate.status,
      });
    } else {
      setFormData({
        rateType: '',
        valueDate: new Date(),
        rate: '',
        status: 'Active',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRate(null);
  };

  const handleSave = () => {
    // TODO: Implement save logic
    handleCloseDialog();
  };

  const handleDelete = () => {
    if (selectedRate) {
      // TODO: Implement delete logic
      handleCloseDialog();
    }
  };

  const filteredRates = rates.filter(rate => {
    const matchesType = !searchRateType || rate.rateType.toLowerCase().includes(searchRateType.toLowerCase());
    const matchesDate = !searchValueDate || 
      rate.valueDate.toDateString() === searchValueDate.toDateString();
    return matchesType && matchesDate;
  });
  const paginatedRates = filteredRates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/dashboard">
          Home
        </MuiLink>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/parameters">
          Parameters
        </MuiLink>
        <Typography color="text.primary">Interest Rate Maintenance</Typography>
      </Breadcrumbs>
      <Typography variant="h4" gutterBottom>
        Interest Rate Maintenance
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<TableChartIcon />}
          sx={{
            borderRadius: 2,
            fontWeight: 500,
            bgcolor: '#fff',
            color: '#1E293B',
            borderColor: '#E5E7EB',
            textTransform: 'none',
            boxShadow: 'none',
            px: 2.5,
            '&:hover': { bgcolor: '#F3F4F6', borderColor: '#6366F1' }
          }}
        >
          Export to Excel
        </Button>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          sx={{
            borderRadius: 2,
            fontWeight: 500,
            bgcolor: '#fff',
            color: '#1E293B',
            borderColor: '#E5E7EB',
            textTransform: 'none',
            boxShadow: 'none',
            px: 2.5,
            '&:hover': { bgcolor: '#F3F4F6', borderColor: '#6366F1' }
          }}
        >
          Print
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('add')}
          color="primary"
          sx={{
            fontWeight: 600,
            px: 4,
            borderRadius: 2,
            boxShadow: '0 1px 2px 0 rgba(59,130,246,0.08)',
            textTransform: 'none',
            '&:hover': { bgcolor: '#2563EB' }
          }}
        >
          Add New Rate
        </Button>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          alignItems: 'center',
          gap: 2,
          mb: 3,
          p: 2,
          bgcolor: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: 3,
          boxShadow: '0 1px 3px 0 rgba(0,0,0,0.03)',
        }}
      >
        <TextField
          placeholder="Search by Rate Name..."
          value={searchRateType}
          onChange={(e) => setSearchRateType(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#6B7280' }} />
              </InputAdornment>
            ),
            sx: { bgcolor: '#fff', borderRadius: 2, height: 40 }
          }}
          sx={{ minWidth: 240, maxWidth: 320 }}
        />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Value Date"
            value={searchValueDate}
            onChange={(newValue) => setSearchValueDate(newValue)}
            slotProps={{ textField: { size: 'small', sx: { minWidth: 180, maxWidth: 220, bgcolor: '#fff', borderRadius: 2, height: 40 } } }}
          />
        </LocalizationProvider>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          color="primary"
          startIcon={<SearchIcon />}
          size="medium"
          sx={{ fontWeight: 500, px: 3, borderRadius: 2, boxShadow: 'none', textTransform: 'none', height: 40, minWidth: 100 }}
        >
          Search
        </Button>
        <Button
          variant="contained"
          color="warning"
          startIcon={<ClearIcon />}
          onClick={() => { setSearchRateType(''); setSearchValueDate(null); }}
          size="medium"
          sx={{ fontWeight: 500, px: 3, borderRadius: 2, boxShadow: 'none', textTransform: 'none', height: 40, minWidth: 100 }}
        >
          Reset
        </Button>
      </Box>
      <Paper 
        sx={{ 
          p: 2, 
          mb: 3,
          borderRadius: 3,
          boxShadow: '0 2px 8px 0 rgba(99,102,241,0.06)',
          border: '1px solid #E5E7EB',
          background: '#fff',
          mt: 2
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Rate ID</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Rate Type</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Value Date</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Rate</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F9FAFB', color: '#1E293B', fontSize: '1rem', py: 1.2 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRates.map((rate, idx) => (
              <TableRow
                key={rate.rateId}
                hover
                sx={{
                  '&:hover': { bgcolor: '#F5F6FF' },
                  borderBottom: idx !== paginatedRates.length - 1 ? '1px solid #E5E7EB' : 'none',
                  transition: 'background 0.2s',
                }}
              >
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{rate.rateId}</TableCell>
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{rate.rateType}</TableCell>
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{rate.valueDate.toLocaleDateString()}</TableCell>
                <TableCell sx={{ py: 1, color: '#374151', fontSize: '0.98rem' }}>{rate.rate.toFixed(4)}</TableCell>
                <TableCell sx={{ py: 1 }}>
                  <Chip 
                    label={rate.status} 
                    size="small"
                    sx={{ 
                      bgcolor: rate.status === 'Active' ? '#D1FAE5' : '#FEE2E2',
                      color: rate.status === 'Active' ? '#059669' : '#DC2626',
                      fontWeight: 500
                    }} 
                  />
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <IconButton 
                    size="small" 
                    onClick={() => handleOpenDialog('view', rate)}
                    sx={{ color: '#6366F1' }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => handleOpenDialog('edit', rate)}
                    sx={{ color: '#6366F1' }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => handleOpenDialog('delete', rate)}
                    sx={{ color: '#DC2626' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredRates.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#374151',
          fontWeight: 600,
          borderBottom: '1px solid #E5E7EB',
          pb: 2
        }}>
          {dialogType === 'add' && 'Add New Rate'}
          {dialogType === 'edit' && 'Edit Rate'}
          {dialogType === 'view' && 'View Rate Details'}
          {dialogType === 'delete' && 'Delete Rate'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {dialogType !== 'delete' ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Rate ID"
                  value={selectedRate?.rateId || 'Auto-generated'}
                  fullWidth
                  disabled
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E5E7EB',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Rate Type</InputLabel>
                  <Select
                    value={formData.rateType}
                    onChange={(e) => setFormData({ ...formData, rateType: e.target.value })}
                    label="Rate Type"
                    disabled={dialogType === 'view'}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#6366F1',
                      },
                    }}
                  >
                    {rateTypes.map((type) => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Value Date"
                    value={formData.valueDate}
                    onChange={(newValue) => setFormData({ ...formData, valueDate: newValue || new Date() })}
                    slotProps={{ 
                      textField: { 
                        fullWidth: true,
                        disabled: dialogType === 'view',
                        sx: {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E5E7EB',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#6366F1',
                          },
                        }
                      } 
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Rate (%)"
                  type="number"
                  value={formData.rate}
                  onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                  fullWidth
                  disabled={dialogType === 'view'}
                  inputProps={{ step: "0.0001" }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E5E7EB',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366F1',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    label="Status"
                    disabled={dialogType === 'view'}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#6366F1',
                      },
                    }}
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {selectedRate && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      label="Creation Date"
                      value={selectedRate.creationDate.toLocaleDateString()}
                      fullWidth
                      disabled
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E5E7EB',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Last Updated Date"
                      value={selectedRate.lastUpdatedDate.toLocaleDateString()}
                      fullWidth
                      disabled
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E5E7EB',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Created By"
                      value={selectedRate.createdBy}
                      fullWidth
                      disabled
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E5E7EB',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Last Updated By"
                      value={selectedRate.lastUpdatedBy}
                      fullWidth
                      disabled
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E5E7EB',
                        },
                      }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          ) : (
            <Typography sx={{ color: '#374151' }}>
              Are you sure you want to delete this rate? This action cannot be undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E7EB' }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{
              color: '#374151',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                bgcolor: '#F5F6FF',
              },
            }}
          >
            Cancel
          </Button>
          {dialogType === 'delete' ? (
            <Button 
              onClick={handleDelete} 
              color="error"
              sx={{
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Delete
            </Button>
          ) : (
            <Button 
              onClick={handleSave} 
              variant="contained"
              sx={{
                bgcolor: '#6366F1',
                '&:hover': {
                  bgcolor: '#4F46E5',
                },
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InterestRateMaintenance; 